// The loop.
//
// It owns the canvas, the socket and the input, and it publishes exactly one
// thing to the rest of the client: a HUD model. Nothing in the UI reaches back
// in except through `commands`, so the DOM cannot make the game do anything the
// keyboard could not.
//
// It is never authoritative — there is no prediction here, because at 20 Hz the
// reconciliation machinery costs far more code than the few pixels it buys.

import { DESIGN_H, DESIGN_W, VIEW_H, VIEW_W, setViewSize } from "./render/view";
import { PALETTE } from "../content/palette";
import { buildWorldMap } from "../sim/map";
import type { PlayerView, ServerMessage, Snapshot } from "../shared/protocol";
import { Input } from "./input";
import { reloadIfStale } from "./build";
import { Net } from "./net";
import { Fx } from "./render/fx";
import { cameraFor, drawScene } from "./render/scene";
import { SnapshotView } from "./render/snapshot-view";
import type { HudCommands } from "./ui/hud";
import { emptyHud, hudFontPx, shopReply, vitalsFrom, withChatLine, type HudModel } from "./ui/hud-model";
import { createStore, type Store } from "./ui/store";

/** How long the shopkeeper's line stays up, and how long the fuller notice
 *  about a world with no room in it does. */
const NOTE_SECONDS = 2;
const FULL_NOTE_SECONDS = 6;

export interface Game {
  /** The only thing the DOM reads. */
  hud: Store<HudModel>;
  /** The only thing the DOM can do. */
  commands: HudCommands;
  stop(): void;
}

const selfOf = (snap: Snapshot): PlayerView | undefined => snap.players.find((p) => p.id === snap.selfId);

export function startGame(canvas: HTMLCanvasElement, slug: string): Game {
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) throw new Error("no 2d context");
  // Narrowed once, because the frame closure below outlives the check.
  const ctx: CanvasRenderingContext2D = context;

  const map = buildWorldMap();
  const fx = new Fx();
  const view = new SnapshotView();
  const hud = createStore<HudModel>(emptyHud(slug));

  let snapshot: Snapshot | null = null;
  let noteLeft = 0;
  let frameId = 0;
  let running = true;
  let last = performance.now();

  /** Pick a logical framebuffer that matches the window's shape, then a whole
   *  integer upscale to fill it. The backing store is a 2x supersample of the
   *  logical view, so the type stays legible without the world going soft. */
  function resize(): void {
    const ratio = window.innerWidth / Math.max(1, window.innerHeight);
    setViewSize(Math.round(DESIGN_H * ratio) || DESIGN_W, DESIGN_H);
    canvas.width = VIEW_W * 2;
    canvas.height = VIEW_H * 2;
    const scale = Math.max(1, Math.min(window.innerWidth / VIEW_W, window.innerHeight / VIEW_H));
    canvas.style.width = `${Math.round(VIEW_W * scale)}px`;
    canvas.style.height = `${Math.round(VIEW_H * scale)}px`;
    // The HUD is DOM and sits inside the canvas's box, so it grows from the
    // same upscale rather than from a second idea of how big the picture is.
    document.documentElement.style.setProperty("--hud-font", `${hudFontPx(scale)}px`);
  }

  function say(text: string, seconds: number): void {
    noteLeft = seconds;
    hud.patch({ note: text });
  }

  function onMessage(msg: ServerMessage): void {
    switch (msg.t) {
      case "welcome":
        // A world running a different bundle than this page is a page that has
        // to go and get the new one. Checked here because this is the moment a
        // reload is free: just connected, standing still, nothing local to lose.
        if (reloadIfStale(msg.buildVersion)) return;
        hud.patch({ slug: msg.slug, chat: msg.chat, maintenance: msg.maintenance ?? "" });
        break;
      case "maintenance":
        hud.patch({ maintenance: msg.message ?? "" });
        // The world dropped every held input when the lock started.
        if (msg.message === null) net.resyncIntent();
        break;
      case "chat":
        hud.patch({ chat: withChatLine(hud.get().chat, msg.line) });
        break;
      case "snapshot":
        snapshot = msg.snap;
        fx.add(msg.snap.events);
        break;
      case "shop":
        say(shopReply(msg.result), NOTE_SECONDS);
        break;
      case "full":
        say(`the world is full (${msg.online}/${msg.cap})`, FULL_NOTE_SECONDS);
        break;
      default:
        break;
    }
  }

  const net = new Net(onMessage);
  const input = new Input({
    move: (intent) => net.sendIntent(intent.mx, intent.my),
    attack: () => net.send({ t: "attack" }),
    buy: () => net.send({ t: "buy" }),
    openChat: () => hud.patch({ typing: true }),
  });

  const commands: HudCommands = {
    move: (intent) => input.setTouch(intent),
    attack: () => net.send({ t: "attack" }),
    buy: () => net.send({ t: "buy" }),
    startTyping() {
      input.release();
      hud.patch({ typing: true });
    },
    say(text) {
      net.send({ t: "chat", text });
      commands.stopTyping();
    },
    stopTyping() {
      hud.patch({ typing: false });
      // Focus goes back to the picture, so the next key is a game key again
      // rather than one nobody is listening for.
      canvas.focus();
    },
  };

  function frame(now: number): void {
    if (!running) return;
    const dt = Math.min(0.1, (now - last) / 1000);
    last = now;

    if (noteLeft > 0) {
      noteLeft -= dt;
      if (noteLeft <= 0) hud.patch({ note: "" });
    }
    fx.step(dt);

    ctx.setTransform(2, 0, 0, 2, 0, 0);
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = PALETTE.void;
    ctx.fillRect(0, 0, VIEW_W, VIEW_H);

    const latest = snapshot;
    const snap = latest ? view.advance(latest, dt) : null;
    const self = snap && selfOf(snap);
    if (snap && self) {
      const camera = cameraFor(self.x, self.y);
      drawScene(ctx, map, snap, self, camera, now / 1000, input.intent());
      fx.draw(ctx, camera);
    }

    // One patch a frame, and the store drops the ones that changed nothing —
    // which is nearly all of them, because these numbers move at 20 Hz at most.
    hud.patch({
      connected: net.open,
      entering: !snap || !self,
      ...(snap ? vitalsFrom(snap) : {}),
    });
    frameId = requestAnimationFrame(frame);
  }

  // Touch controls show on a device whose main pointer is a finger, and on any
  // other device the moment somebody touches the picture.
  const onPointerDown = (ev: PointerEvent): void => {
    if (ev.pointerType === "touch") hud.patch({ touch: true });
  };
  hud.patch({ touch: window.matchMedia?.("(pointer: coarse)").matches ?? false });
  window.addEventListener("pointerdown", onPointerDown);

  resize();
  window.addEventListener("resize", resize);
  const intent = input.intent();
  net.sendIntent(intent.mx, intent.my);
  net.connect();
  frameId = requestAnimationFrame(frame);

  return {
    hud,
    commands,
    stop() {
      running = false;
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointerdown", onPointerDown);
      input.stop();
      net.close();
    },
  };
}
